export interface CacheConfig {
  maxAge?: number;
  maxSize?: number;
  enableLogging?: boolean;
}

export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
  metadata?: Record<string, any>;
}

export interface PaginationData<T> {
  items: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
  metadata?: Record<string, any>;
}

export interface SearchCache<T> {
  query: string;
  results: T[];
  timestamp: number;
  totalResults: number;
}

export class UniversalCacheManager {
  private cache: Map<string, CacheEntry<any>> = new Map();
  private config: Required<CacheConfig>;

  constructor(config: CacheConfig = {}) {
    this.config = {
      maxAge: config.maxAge || 5 * 60 * 1000,
      maxSize: config.maxSize || 1000,
      enableLogging: config.enableLogging !== false,
    };
  }

  set<T>(
    key: string,
    data: T,
    customTTL?: number,
    metadata?: Record<string, any>
  ): void {
    const now = Date.now();
    const ttl = customTTL || this.config.maxAge;

    const entry: CacheEntry<T> = {
      data,
      timestamp: now,
      expiresAt: now + ttl,
      metadata,
    };

    this.cache.set(key, entry);
    this.enforceMaxSize();
  }

  setWithShortTTL<T>(
    key: string,
    data: T,
    metadata?: Record<string, any>
  ): void {
    const SHORT_TTL = 10 * 1000;
    this.set(key, data, SHORT_TTL, metadata);
  }

  setWithMediumTTL<T>(
    key: string,
    data: T,
    metadata?: Record<string, any>
  ): void {
    const MEDIUM_TTL = 60 * 1000;
    this.set(key, data, MEDIUM_TTL, metadata);
  }

  setWithLongTTL<T>(
    key: string,
    data: T,
    metadata?: Record<string, any>
  ): void {
    const LONG_TTL = 10 * 60 * 1000;
    this.set(key, data, LONG_TTL, metadata);
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    if (Date.now() > entry.expiresAt) {
      this.delete(key);
      return null;
    }

    return entry.data as T;
  }

  has(key: string): boolean {
    const entry = this.cache.get(key);

    if (!entry) {
      return false;
    }

    if (Date.now() > entry.expiresAt) {
      this.delete(key);
      return false;
    }

    return true;
  }

  delete(key: string): boolean {
    const deleted = this.cache.delete(key);

    return deleted;
  }

  clear(): void {
    this.cache.clear();
  }

  clearByPattern(pattern: string): number {
    const keysToDelete: string[] = [];

    Array.from(this.cache.keys()).forEach((key) => {
      if (key.startsWith(pattern)) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach((key) => this.cache.delete(key));

    return keysToDelete.length;
  }

  async getOrSet<T>(
    key: string,
    fetchFunction: () => Promise<T>,
    customTTL?: number,
    metadata?: Record<string, any>
  ): Promise<T> {
    const cached = this.get<T>(key);
    if (cached !== null) {
      return cached;
    }

    try {
      const data = await fetchFunction();
      this.set(key, data, customTTL, metadata);
      return data;
    } catch (error) {
      if (this.config.enableLogging) {
        console.error(`❌ Cache FETCH ERROR for ${key}:`, error);
      }
      throw error;
    }
  }

  async getOrSetWithShortTTL<T>(
    key: string,
    fetchFunction: () => Promise<T>,
    metadata?: Record<string, any>
  ): Promise<T> {
    const SHORT_TTL = 10 * 1000;
    return this.getOrSet(key, fetchFunction, SHORT_TTL, metadata);
  }

  async getOrSetWithMediumTTL<T>(
    key: string,
    fetchFunction: () => Promise<T>,
    metadata?: Record<string, any>
  ): Promise<T> {
    const MEDIUM_TTL = 60 * 1000;
    return this.getOrSet(key, fetchFunction, MEDIUM_TTL, metadata);
  }

  async getOrSetWithLongTTL<T>(
    key: string,
    fetchFunction: () => Promise<T>,
    metadata?: Record<string, any>
  ): Promise<T> {
    const LONG_TTL = 10 * 60 * 1000;
    return this.getOrSet(key, fetchFunction, LONG_TTL, metadata);
  }

  setPaginatedData<T>(
    baseKey: string,
    page: number,
    items: T[],
    pagination: { total: number; limit: number; hasMore: boolean },
    customTTL?: number
  ): void {
    const paginationData: PaginationData<T> = {
      items,
      pagination: {
        page,
        limit: pagination.limit,
        total: pagination.total,
        totalPages: Math.ceil(pagination.total / pagination.limit),
        hasMore: pagination.hasMore,
      },
    };

    const key = `${baseKey}_page_${page}`;
    this.set(key, paginationData, customTTL);

    this.set(`${baseKey}_total`, pagination.total, customTTL);
  }

  getPaginatedData<T>(baseKey: string, page: number): PaginationData<T> | null {
    const key = `${baseKey}_page_${page}`;
    return this.get<PaginationData<T>>(key);
  }

  getAllPaginatedPages<T>(baseKey: string): PaginationData<T>[] {
    const pages: PaginationData<T>[] = [];

    Array.from(this.cache.keys()).forEach((key) => {
      if (key.startsWith(`${baseKey}_page_`)) {
        const data = this.get<PaginationData<T>>(key);
        if (data) {
          pages.push(data);
        }
      }
    });

    return pages.sort((a, b) => a.pagination.page - b.pagination.page);
  }

  invalidatePaginatedData(baseKey: string): void {
    const keysToDelete: string[] = [];

    Array.from(this.cache.keys()).forEach((key) => {
      if (key.startsWith(baseKey)) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach((key) => this.delete(key));
  }

  setSearchResults<T>(
    query: string,
    results: T[],
    totalResults: number,
    customTTL?: number
  ): void {
    const searchData: SearchCache<T> = {
      query,
      results,
      timestamp: Date.now(),
      totalResults,
    };

    const key = `search_${this.hashString(query)}`;
    this.set(key, searchData, customTTL);
  }

  getSearchResults<T>(query: string): SearchCache<T> | null {
    const key = `search_${this.hashString(query)}`;
    return this.get<SearchCache<T>>(key);
  }

  setFormData(
    formId: string,
    data: Record<string, any>,
    customTTL?: number
  ): void {
    const key = `form_${formId}`;
    this.set(key, data, customTTL);
  }

  getFormData(formId: string): Record<string, any> | null {
    const key = `form_${formId}`;
    return this.get<Record<string, any>>(key);
  }

  clearFormData(formId: string): void {
    const key = `form_${formId}`;
    this.delete(key);
  }

  setApiResponse<T>(
    endpoint: string,
    params: Record<string, any> = {},
    response: T,
    customTTL?: number
  ): void {
    const key = this.generateApiKey(endpoint, params);
    this.set(key, response, customTTL, { endpoint, params });
  }

  getApiResponse<T>(
    endpoint: string,
    params: Record<string, any> = {}
  ): T | null {
    const key = this.generateApiKey(endpoint, params);
    return this.get<T>(key);
  }

  async cacheApiCall<T>(
    endpoint: string,
    params: Record<string, any> = {},
    apiCall: () => Promise<T>,
    customTTL?: number
  ): Promise<T> {
    const key = this.generateApiKey(endpoint, params);
    return this.getOrSet(key, apiCall, customTTL, { endpoint, params });
  }

  getStats(): {
    totalEntries: number;
    cacheSize: string;
    oldestEntry: Date | null;
    newestEntry: Date | null;
    expiredEntries: number;
  } {
    let oldest: number | null = null;
    let newest: number | null = null;
    let expiredCount = 0;
    const now = Date.now();

    Array.from(this.cache.values()).forEach((entry) => {
      if (!oldest || entry.timestamp < oldest) {
        oldest = entry.timestamp;
      }
      if (!newest || entry.timestamp > newest) {
        newest = entry.timestamp;
      }
      if (now > entry.expiresAt) {
        expiredCount++;
      }
    });

    return {
      totalEntries: this.cache.size,
      cacheSize: this.formatBytes(this.estimateCacheSize()),
      oldestEntry: oldest ? new Date(oldest) : null,
      newestEntry: newest ? new Date(newest) : null,
      expiredEntries: expiredCount,
    };
  }

  cleanExpired(): number {
    const now = Date.now();
    const expiredKeys: string[] = [];

    Array.from(this.cache.entries()).forEach(([key, entry]) => {
      if (now > entry.expiresAt) {
        expiredKeys.push(key);
      }
    });

    expiredKeys.forEach((key) => this.cache.delete(key));

    return expiredKeys.length;
  }

  private enforceMaxSize(): void {
    if (this.cache.size <= this.config.maxSize) {
      return;
    }

    const entries = Array.from(this.cache.entries()).sort(
      ([, a], [, b]) => a.timestamp - b.timestamp
    );

    const entriesToRemove = entries.slice(
      0,
      this.cache.size - this.config.maxSize
    );

    entriesToRemove.forEach(([key]) => {
      this.cache.delete(key);
    });
  }

  private generateApiKey(
    endpoint: string,
    params: Record<string, any>
  ): string {
    const paramString = Object.keys(params)
      .sort()
      .map((key) => `${key}=${params[key]}`)
      .join("&");

    return `api_${endpoint}_${this.hashString(paramString)}`;
  }

  private hashString(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
  }

  private estimateCacheSize(): number {
    let size = 0;
    Array.from(this.cache.values()).forEach((entry) => {
      size += JSON.stringify(entry).length * 2; // Rough estimate (UTF-16)
    });
    return size;
  }

  private formatBytes(bytes: number): string {
    const sizes = ["Bytes", "KB", "MB", "GB"];
    if (bytes === 0) return "0 Bytes";
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + " " + sizes[i];
  }
}

export const apiCache = new UniversalCacheManager({
  maxAge: 5 * 60 * 1000,
  maxSize: 500,
});

export const paginationCache = new UniversalCacheManager({
  maxAge: 3 * 60 * 1000,
  maxSize: 200,
});

export const searchCache = new UniversalCacheManager({
  maxAge: 2 * 60 * 1000,
  maxSize: 100,
});

export const formCache = new UniversalCacheManager({
  maxAge: 30 * 60 * 1000,
  maxSize: 50,
});

export default UniversalCacheManager;
